import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  ComboboxItem,
  Group,
  Menu,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import {
  IconClipboardData,
  IconExternalLink,
  IconPencil,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";

import { WithAppointmentsNavbar } from "@layouts/WithAppointmentsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { LastUpdatedTimestamp } from "@components/LastUpdated";
import { PatientNotesModal } from "@components/PatientNotesModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TruncatedCell } from "@components/Table/TruncatedCell";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { DEFAULT_PAGINATION, PAGE_TITLE, QP } from "@constants/index";

import { useToggleConsultationRelease } from "@hooks/mutations/consultation/useToggleConsultationRelease";
import { followUpQKeys } from "@hooks/queries/followUp/queryKeys";
import { useGetFollowUps } from "@hooks/queries/followUp/useGetFollowUps";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { FollowUp as IFollowUp } from "@customTypes/followUp";

import { CloseFollowUpModal } from "../components/CloseFollowUpModal";
import { EditFollowUpModal } from "../components/EditFollowUpModal";

import { StyledFollowUp } from "./FollowUp.styled";


const billStatusColors = new Map([
  ["pending", { c: "yellow.9", bg: "yellow.0" }],
  ["accepted", { c: "accent.9", bg: "accent.0" }],
  ["paid", { c: "green.9", bg: "green.0" }],
]);

export const FollowUp = () => {
  const { t, i18n } = useTranslation();
  useDocumentTitle(`${t("follow-up")} • ${PAGE_TITLE}`);

  const [searchFilter, setSearchFilter] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [closeModalOpened, { open: openCloseModal, close: closeCloseModal }] =
    useDisclosure(false);
  const [
    patientNotesModalOpened,
    { open: openPatientNotesModal, close: closePatientNotesModal },
  ] = useDisclosure(false);
  const [currentFollowUp, setCurrentFollowUp] = useState<Partial<IFollowUp>>(
    {}
  );
  const [currentConsultationId, setCurrentConsultationId] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [doctors, setDoctors] = useState<ComboboxItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { columnCount, tableRef } = useTableColumnCount();

  const { data: doctorsData } = useGetUsers({ role: "doctor" });

  const {
    data: followUpData,
    isLoading: isFollowUpLoading,
    isSuccess: isFollowUpSuccess,
    isRefetching: isRefetching,
    fetchStatus,
    isError: isFollowUpError,
    refetch,
  } = useGetFollowUps({
    search: searchFilter,
    pagination: { page: currentPage, pageSize: DEFAULT_PAGINATION.pageSize },
    doctor: doctorFilter,
  });
  // const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing: isRefetching });

  const {
    mutate: toggleConsultationRelease,
    isLoading: isToggleConsultationReleaseLoading,
  } = useToggleConsultationRelease();

  const handleErrors = useQueryErrorHandling();

  const followUpRows = useMemo(() => {
    return followUpData?.newData?.map((item, index) => {
      return (
        <Table.Tr key={index} pos={"relative"}>
          <Table.Td>
            <Text fz={14} lh={1.5}>
              {item.patientName}
            </Text>
          </Table.Td>
          <Table.Td>
            <Stack gap={0}>
              <Text fz={14} lh={1.5}>
                {formatDateToString(item.appointmentDate!)}
              </Text>
              <Text fz={13} c="dimmed" lh={1.5}>
                {item.appointmentDate?.toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            </Stack>
          </Table.Td>
          <Table.Td>
            <Text fz={14} lh={1.5}>
              <TruncatedCell value={item.serviceType} />
            </Text>
          </Table.Td>
          <Table.Td>
            <Button
              w={"auto"}
              tt={"capitalize"}
              variant="light"
              size="compact-sm"
              disabled={!item.consultationId}
              color={
                item.consultationVisibility === "released" ? "green" : "red"
              }
              loading={
                isToggleConsultationReleaseLoading &&
                currentConsultationId === item.consultationId
              }
              onClick={e => {
                setCurrentConsultationId(item.consultationId as string);
                const id = showLoadingNotification({});
                toggleConsultationRelease(
                  {
                    consultationId: item.consultationId as string,
                    patientId: item.patientId as string,
                    visibility:
                      item.consultationVisibility === "released"
                        ? "unrelease"
                        : "release",
                  },
                  {
                    onSuccess: () => {
                      showSuccessNotification({
                        id,
                        title: "success",
                        message: "update-consultation-success",
                      });
                      // queryClient.invalidateQueries({
                      //   queryKey: followUpQKeys.all(),
                      // });
                      refetch();
                    },
                    onError: (res: any) => {
                      handleErrors(res, "mutation", id);
                    },
                  }
                );
                e.stopPropagation();
              }}>
              {t(`${item?.consultationVisibility ?? ""}`)}
            </Button>
          </Table.Td>
          <Table.Td>
            {item?.billStatus ? (
              <Badge
                fz={12}
                size="md"
                c={billStatusColors.get(item.billStatus)?.c}
                bg={billStatusColors.get(item.billStatus)?.bg}>
                {t(`${item.billStatus}`)}
              </Badge>
            ) : null}
          </Table.Td>
          <Table.Td>
            <TruncatedCell value={item.doctor?.fullName} />
          </Table.Td>
          <Table.Td>
            <Stack gap={0}>
              <Text fz={14} lh={1.5}>
                {formatDateToString(item.followUpDate!)}
              </Text>
              <Text fz={13} c="dimmed" lh={1.5}>
                {item.followUpDate?.toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            </Stack>
          </Table.Td>
          <Table.Td>
            <Group justify="center">
              <Menu withArrow position="bottom-end">
                <Menu.Target>
                  <Button
                    variant="outline"
                    size="compact-sm"
                    className="table-controls">
                    {t("action")}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconExternalLink size={16} stroke={1.5} />}
                    onClick={() => {
                      navigate(
                        `/patients/${item.patientId}/consultation/general/${item.consultationId}`
                      );
                    }}>
                    {t("view-consultation")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconClipboardData size={16} stroke={1.5} />}
                    onClick={() => {
                      setCurrentFollowUp(item);
                      openPatientNotesModal();
                    }}>
                    {t("open-patient-notes")}
                  </Menu.Item>
                  <PermissionGate permission="appointments.edit"><Menu.Item
                    leftSection={<IconPencil size={16} stroke={1.5} />}
                    onClick={() => {
                      setCurrentFollowUp(item);
                      openEditModal();
                    }}>
                    {t("edit-follow-up")}
                  </Menu.Item></PermissionGate>
                  <Menu.Divider />
                  <Menu.Item
                    disabled={item.billStatus !== "paid"}
                    color="primary"
                    leftSection={<IconX size={16} stroke={1.5} />}
                    onClick={() => {
                      setCurrentFollowUp(item);
                      openCloseModal();
                    }}>
                    {t("close-follow-up")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <LastUpdatedTimestamp
                updatedDate={item.updatedAt!}
                className="last-updated"
              />
            </Group>
          </Table.Td>
        </Table.Tr>
      );
    });
  }, [
    i18n.language,
    followUpData,
    currentConsultationId,
    isToggleConsultationReleaseLoading,
  ]);

  useEffect(() => {
    if (doctorsData) {
      const newData = doctorsData.map(doctor => ({
        label: doctor.fullName,
        value: doctor.id,
      }));

      setDoctors(newData);
    }
  }, [doctorsData]);

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithAppointmentsNavbar>
        <StyledFollowUp>
          <Group gap={"xs"}>
            <TextInput
              leftSection={<IconSearch size={16} stroke={1.5} />}
              placeholder={t("search-for-patient")}
              miw={400}
              value={searchFilter}
              onChange={e => {
                setSearchFilter(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Tooltip
              label={t("refresh-data")}
              position="right-end"
              fz={12}
              events={{ focus: true, hover: true, touch: true }}>
              <ActionIcon
                ref={buttonRef}
                variant={"default"}
                size={"lg"}
                onClick={() => {
                  queryClient.invalidateQueries(followUpQKeys.all());
                }}>
                <IconRefresh
                  className={"refresh-icon"}
                  color="var(--mantine-color-black)"
                  size={16}
                />
              </ActionIcon>
            </Tooltip>
          </Group>
          <EditFollowUpModal
            opened={editModalOpened}
            close={closeEditModal}
            followUp={currentFollowUp}
          />
          <CloseFollowUpModal
            opened={closeModalOpened}
            close={closeCloseModal}
            followUp={currentFollowUp}
          />
          <PatientNotesModal
            followUp={currentFollowUp}
            opened={patientNotesModalOpened}
            close={closePatientNotesModal}
          />
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
            <Table
              ref={tableRef}
              mt={"2rem"}
              withRowBorders={true}
              miw={1360}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th>{t("name")}</Table.Th>
                  <Table.Th>{t("appointment-date")}</Table.Th>
                  <Table.Th>{t("service-type")}</Table.Th>
                  <Table.Th>{t("visibility")}</Table.Th>
                  <Table.Th>{t("bill-status")}</Table.Th>
                  <Table.Th>
                    <Select
                      data={doctors}
                      placeholder={t("doctor")}
                      value={doctorFilter ?? null}
                      clearable
                      onChange={(_val, option) => {
                        setDoctorFilter(option?.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Table.Th>
                  <Table.Th>{t("follow-up-date")}</Table.Th>
                  <Table.Th ta={"right"}>{t("last-updated-action")}</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isFollowUpLoading || fetchStatus === "fetching",
                  isError: isFollowUpError,
                  isSuccess: isFollowUpSuccess,
                  dataLength: followUpData?.newData?.length ?? 0,
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={t("empty-data")}
                    />
                  ),
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: [
                          "lg",
                          "md",
                          "lg",
                          "md",
                          "md",
                          "lg",
                          "md",
                          "md",
                        ],
                        rightAlignLastColumn: true,
                      }}
                    />
                  ),
                  dataComponent: followUpRows,
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <TablePagination
            paginationData={followUpData?.pagination || {}}
            paginatedRows={followUpData?.newData?.length || 0}
            onPageChange={setCurrentPage}
            isLoading={isFollowUpLoading || fetchStatus === "fetching"}
          />
        </StyledFollowUp>
      </WithAppointmentsNavbar>
    </WithSidebar>
  );
};
